import { useState } from "react";
import { ListChecks, Wand2, Loader2, PlusCircle, Trash2, Sparkles, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_ITEMS = 5;

// Sub-topic → tailwind pill colours. Matches sidebar accent choices.
const PILL_STYLES = {
  // Market group
  "Market":               "bg-teal-100    text-teal-700",
  "Value Chain":          "bg-emerald-100 text-emerald-700",
  "Competitor Landscape": "bg-cyan-100    text-cyan-700",
  // Clinical group
  "Clinical Proof":       "bg-teal-100    text-teal-700",
  "Regulatory Route":     "bg-emerald-100 text-emerald-700",
  "Access & HEOR":        "bg-amber-100   text-amber-700",
  // Technology group
  "Technology":           "bg-teal-100    text-teal-700",
  "Defensibility":        "bg-cyan-100    text-cyan-700",
  "Business Model":       "bg-amber-100   text-amber-700",
  // Milestones group
  "Milestones":           "bg-teal-100    text-teal-700",
  "Team":                 "bg-emerald-100 text-emerald-700",
  "Funding Strategy":     "bg-amber-100   text-amber-700",
};

/** Split "[Sub-topic] rest of the text" into { pill, text }. Returns null pill if no match. */
function splitPrefix(raw) {
  const m = /^\s*\[([^\]]+)\]\s*(.*)$/s.exec(raw || "");
  if (!m) return { pill: null, text: raw || "" };
  return { pill: m[1].trim(), text: m[2].trim() };
}

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

/**
 * Clustered To-Do tab (4th tab of each Strategy group).
 *
 * ONE single list of max 5 priority to-dos synthesised across the 3 sub-topics
 * of the group. The AI-suggest button asks the backend for cross-topic
 * priorities in one shot. Manual add allowed while list < 5.
 *
 * Props:
 *   items         — array of { id, text, done }
 *   setItems      — setter
 *   persist       — (overrides) => Promise, called after every write
 *   endpointKey   — "market" | "clinical" | "technology" | "milestones"
 *   getSuggestBody(existing) — returns POST body for the suggest-followups endpoint
 *                              in cluster-priority mode (`mode: "cluster_priorities"`)
 *   onGenerateCard — () => void — triggers the shell's card modal + generation
 *   generatingCard — boolean (parent-owned)
 *   hasCard       — boolean, does a card exist (parent-owned)
 *   onOpenCard    — () => void — reopen the modal to view the existing card
 *   groupTitle    — copy shown on the Generate button
 */
export default function ClusteredTodoTab({
  items,
  setItems,
  persist,
  endpointKey,
  getSuggestBody,
  onGenerateCard,
  generatingCard,
  hasCard,
  onOpenCard,
  groupTitle,
}) {
  const [busy, setBusy] = useState(false);
  const [inputText, setInputText] = useState("");
  const [editItem, setEditItem] = useState(null);

  const writeItems = (next) => {
    // strip legacy sub_module field on save so we don't grow the doc
    const cleaned = next.map(({ id, text, done }) => ({ id, text, done }));
    setItems(cleaned);
    persist({ followups: cleaned });
  };
  const update = (id, patch) => writeItems(items.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  const remove = (id) => writeItems(items.filter((f) => f.id !== id));

  const addLocal = () => {
    const t = inputText.trim();
    if (!t) return;
    if (items.length >= MAX_ITEMS) {
      toast.error(`Max ${MAX_ITEMS} priorities. Remove one or click Regenerate.`);
      return;
    }
    writeItems([...items, { id: uid(), text: t, done: false }]);
    setInputText("");
  };

  const suggestPriorities = async () => {
    // If the user already has priorities in the list, confirm before overwriting.
    // Regeneration REPLACES the entire list — this protects manually-added or
    // partially-completed items from silent loss.
    if (items.length > 0) {
      const doneCount = items.filter((i) => i.done).length;
      const message = doneCount > 0
        ? `This will replace your current ${items.length} priorities (including ${doneCount} marked as done) with fresh AI suggestions.\n\nContinue?`
        : `This will replace your current ${items.length} priorities with fresh AI suggestions.\n\nContinue?`;
      if (!window.confirm(message)) return;
    }
    setBusy(true);
    try {
      const body = { ...getSuggestBody(items.map((i) => i.text)), mode: "cluster_priorities" };
      const res = await fetch(`${BACKEND_URL}/api/strategy/${endpointKey}/suggest-followups`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("suggest failed");
      const d = await res.json();
      const news = (d.items || []).slice(0, MAX_ITEMS).map((t) => ({ id: uid(), text: t, done: false }));
      if (news.length === 0) {
        toast.info("No suggestions returned");
      } else {
        // Replace list with the fresh cross-topic priorities.
        writeItems(news);
        toast.success(`Loaded ${news.length} priority to-dos`);
      }
    } catch (e) {
      toast.error("Failed to get priorities");
    }
    setBusy(false);
  };

  const openCount = items.filter((it) => !it.done).length;
  const atCap = items.length >= MAX_ITEMS;

  return (
    <div data-testid="clustered-todo-tab" className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-lg text-slate-900 flex items-center gap-2">
                <ListChecks className="w-5 h-5 text-teal-600" /> Priority to-dos
              </CardTitle>
              <p className="text-sm text-slate-500 mt-1">
                A single, prioritised list of up to {MAX_ITEMS} actions synthesised across the three topics in this group.
                {items.length > 0 && ` ${openCount} of ${items.length} open.`}
              </p>
            </div>
            <Button
              onClick={suggestPriorities}
              disabled={busy}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="clustered-todo-suggest-priorities-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
              {items.length > 0 ? "Regenerate priorities" : "AI suggest priorities"}
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {items.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg" data-testid="clustered-todo-empty">
              <ListChecks className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No priorities yet.</p>
              <p className="text-xs text-slate-400 mt-1">Click <span className="font-medium">AI suggest priorities</span> to synthesise the top {MAX_ITEMS} actions across the group&apos;s three topics.</p>
            </div>
          ) : (
            <ul className="space-y-2" data-testid="clustered-todo-list">
              {items.map((it, i) => {
                const { pill, text } = splitPrefix(it.text);
                const pillClass = pill ? (PILL_STYLES[pill] || "bg-slate-100 text-slate-600") : "";
                return (
                  <li
                    key={it.id}
                    data-testid={`clustered-todo-item-${it.id}`}
                    onClick={() => setEditItem(it)}
                    className="group cursor-pointer border rounded-lg p-3 bg-white hover:bg-slate-50 hover:shadow-sm transition-all border-l-4 border-l-teal-400 flex items-start gap-3"
                  >
                    <span className="text-xs font-semibold text-teal-600 mt-1 w-5 flex-shrink-0">{i + 1}.</span>
                    <div onClick={(e) => e.stopPropagation()} className="pt-1 flex-shrink-0">
                      <Checkbox
                        checked={!!it.done}
                        onCheckedChange={(v) => update(it.id, { done: !!v })}
                        data-testid={`clustered-todo-done-${it.id}`}
                      />
                    </div>
                    <div className={`flex-1 leading-relaxed text-sm ${it.done ? "line-through text-slate-400" : "text-slate-700"}`}>
                      {pill && (
                        <span
                          className={`inline-block text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 mr-2 align-middle ${pillClass}`}
                          data-testid={`clustered-todo-pill-${it.id}`}
                        >
                          {pill}
                        </span>
                      )}
                      <span>{text}</span>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); remove(it.id); }}
                      className="text-slate-300 hover:text-red-500 flex-shrink-0 p-0.5"
                      data-testid={`clustered-todo-remove-${it.id}`}
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="flex gap-2 pt-3 border-t">
            <Input
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addLocal()}
              placeholder={atCap ? `Max ${MAX_ITEMS} priorities — remove one to add another` : "Add your own priority…"}
              className="text-sm"
              disabled={atCap}
              data-testid="clustered-todo-input"
            />
            <Button
              size="sm"
              onClick={addLocal}
              disabled={atCap || !inputText.trim()}
              data-testid="clustered-todo-add-btn"
            >
              <PlusCircle className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Generate Card CTA */}
      <div className="rounded-2xl bg-gradient-to-r from-teal-50 to-amber-50 border border-teal-200 p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="font-semibold text-slate-900">Wrap up: generate your strategic overview</p>
            <p className="text-sm text-slate-600 mt-0.5">
              A short executive narrative synthesising the three topics into one story, with key strategic insights and watch-outs.
            </p>
          </div>
        </div>
        <div className="flex gap-2 self-stretch md:self-auto">
          {hasCard && (
            <Button
              variant="outline"
              onClick={onOpenCard}
              data-testid="clustered-todo-open-card-btn"
            >
              <FileText className="w-4 h-4 mr-2" /> View existing
            </Button>
          )}
          <Button
            onClick={onGenerateCard}
            disabled={generatingCard}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="clustered-todo-generate-card-btn"
          >
            {generatingCard ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {hasCard ? "Regenerate" : groupTitle || "Generate strategic overview"}
          </Button>
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editItem} onOpenChange={(o) => { if (!o) setEditItem(null); }}>
        <DialogContent className="max-w-lg" data-testid="clustered-todo-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-teal-600">
              <ListChecks className="w-4 h-4" /> Edit to-do
            </DialogTitle>
            <DialogDescription className="sr-only">Edit the to-do text and toggle done state.</DialogDescription>
          </DialogHeader>
          {editItem && (
            <Textarea
              value={editItem.text}
              onChange={(e) => setEditItem({ ...editItem, text: e.target.value })}
              rows={5}
              className="text-sm"
              autoFocus
              data-testid="clustered-todo-edit-textarea"
            />
          )}
          <div className="flex justify-between pt-2 border-t">
            {editItem && (
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <Checkbox
                  checked={!!editItem.done}
                  onCheckedChange={(v) => setEditItem({ ...editItem, done: !!v })}
                />
                Done
              </label>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditItem(null)}>Cancel</Button>
              <Button
                onClick={() => {
                  if (editItem && editItem.text.trim()) {
                    update(editItem.id, { text: editItem.text.trim(), done: !!editItem.done });
                  }
                  setEditItem(null);
                }}
                data-testid="clustered-todo-edit-save-btn"
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
